import { EmptyState } from "@/components/ui/empty-state";
import {
    FileText,
    File,
    BookOpen,
    Link as LinkIcon
} from "lucide-react";
import KnowledgeItemCard from "@/components/knowledge/KnowledgeItemCard";
import { Skeleton } from "@/components/ui/skeleton";
import { KnowledgeItem, type KnowledgeItemType } from "@/hooks/knowledge/useKnowledgeItems";

type FilterType = "all" | "links" | "files" | "snippets";

interface KnowledgeSectionProps {
    title: string;
    subtitle: string;
    items: KnowledgeItem[];
    isLoading: boolean;
    activeFilter: FilterType;
    agentId?: string | null;
    showAddButton?: boolean;
    showRefreshButton?: boolean;
    showEmptyState?: boolean;
    onAddSuccess?: () => void;
    onDelete?: (id: string, type: string, filePath?: string) => Promise<boolean>;
    onEdit?: () => void;
    onRefresh?: () => void;
  
}

const KnowledgeSection = ({
    title,
    subtitle,
    items,
    isLoading,
    activeFilter,
    agentId,
    showAddButton = false,
    showRefreshButton = false,
    showEmptyState = true,
    onAddSuccess,
    onDelete,
    onEdit,
    onRefresh
}: KnowledgeSectionProps) => {
    // Filter items based on the active filter
    const filteredItems = items.filter(item => {
        // Apply type filter
        if (activeFilter === "all") return true;
        if (activeFilter === "links") return item.type === "link";
        if (activeFilter === "files") return item.type === "file";
        if (activeFilter === "snippets") return item.type === "snippet";
        return false;
    });

    const handleDelete = async (id: string, type: string, filePath?: string) => {
        if (onDelete) {
            return await onDelete(id, type, filePath);
        }
        return false;
    };

    if (isLoading) {
        return (
            <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                ))}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Knowledge Items List */}
            {filteredItems.length === 0 ? (
                showEmptyState ? (
                    <EmptyState
                        icon={activeFilter === "all" ? BookOpen : 
                              activeFilter === "links" ? LinkIcon :
                              activeFilter === "files" ? File : FileText}
                        title={`No ${activeFilter === "all" ? "knowledge items" : activeFilter} yet`}
                        description={`${activeFilter === "all" ? "This section doesn't have any knowledge items" : 
                                      `No ${activeFilter} have been added to this section`}.`}
                    />
                ) : null
            ) : (
                <div className="space-y-3">
                    {filteredItems.map((item) => (
                        <KnowledgeItemCard
                            key={item.id}
                            item={item}
                            onDelete={() => handleDelete(item.id, item.type, item.file_path)}
                            onEdit={onEdit}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default KnowledgeSection; 